import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash } from "@phosphor-icons/react";
import { SOCIALS, FOOTER_UPDATED } from "@/components/SiteFooter";
import { toast } from "sonner";

const EMPTY = { about: "", address: "", phone: "", email: "", office_hours: "", copyright_text: "", partners: [], important_links: [], socials: {} };

function Section({ title, hint, children }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
      <h3 className="font-display text-lg font-bold">{title}</h3>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`footer-input-${id}`} {...props} /></div>
);

function LinksEditor({ kind, links, onChange, max }) {
  const update = (i, key, value) => onChange(links.map((l, j) => (j === i ? { ...l, [key]: value } : l)));
  return (
    <div className="space-y-3" data-testid={`footer-editor-${kind}`}>
      {links.map((l, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1.6fr_auto]">
          <Input placeholder="Name" value={l.name} maxLength={80} onChange={(e) => update(i, "name", e.target.value)} data-testid={`footer-${kind}-name-${i}`} />
          <Input placeholder="https://..." value={l.url} maxLength={500} onChange={(e) => update(i, "url", e.target.value)} data-testid={`footer-${kind}-url-${i}`} />
          <Button type="button" variant="outline" size="icon" aria-label="Remove link" onClick={() => onChange(links.filter((_, j) => j !== i))} data-testid={`footer-${kind}-remove-${i}`}>
            <Trash size={16} weight="bold" />
          </Button>
        </div>
      ))}
      {links.length < max && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => onChange([...links, { name: "", url: "" }])} data-testid={`footer-${kind}-add`}>
          <Plus size={14} weight="bold" /> Add link
        </Button>
      )}
    </div>
  );
}

export function FooterSettings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/site-footer").then((r) => setF({ ...EMPTY, ...r.data })); }, []);
  if (!f) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const set = (key) => (e) => setF({ ...f, [key]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { updated_at, updated_by, ...body } = f;
      const clean = (list) => list.filter((l) => l.name.trim() || l.url.trim());
      const { data } = await api.put("/site-footer", { ...body, partners: clean(f.partners), important_links: clean(f.important_links) });
      setF({ ...EMPTY, ...data });
      window.dispatchEvent(new Event(FOOTER_UPDATED));
      toast.success("Footer updated on all pages");
    } catch (err) { toast.error((formatApiError(err.response?.data?.detail) || "Could not save footer").replace(/Value error, /g, "")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="footer-settings">
      <p className="text-sm text-muted-foreground">
        This information appears in the footer of every page, including sign-in pages. Empty sections are hidden.
        {f.updated_at && <> Last updated {new Date(f.updated_at).toLocaleString()}{f.updated_by && ` by ${f.updated_by}`}.</>}
      </p>
      <Section title="About and contact details">
        <div className="space-y-1.5"><Label htmlFor="about">About MELPWU (short)</Label>
          <Textarea id="about" rows={3} maxLength={400} value={f.about} onChange={set("about")} data-testid="footer-input-about" /></div>
        <div className="space-y-1.5"><Label htmlFor="address">Office address</Label>
          <Textarea id="address" rows={2} maxLength={300} value={f.address} onChange={set("address")} data-testid="footer-input-address" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="phone" label="Phone" maxLength={60} value={f.phone} onChange={set("phone")} />
          <Field id="email" label="Email" type="email" maxLength={120} value={f.email} onChange={set("email")} />
          <Field id="office_hours" label="Office hours" placeholder="Mon–Fri, 8:00am – 5:00pm" maxLength={120} value={f.office_hours} onChange={set("office_hours")} />
          <Field id="copyright_text" label="Copyright line (optional)" placeholder="© MELPWU. All rights reserved." maxLength={160} value={f.copyright_text} onChange={set("copyright_text")} />
        </div>
      </Section>
      <Section title="Partner websites" hint="Up to 12. Each link must start with https:// or http://">
        <LinksEditor kind="partners" max={12} links={f.partners} onChange={(partners) => setF({ ...f, partners })} />
      </Section>
      <Section title="Important links" hint="Up to 10, for example the constitution, news page or downloads.">
        <LinksEditor kind="important" max={10} links={f.important_links} onChange={(important_links) => setF({ ...f, important_links })} />
      </Section>
      <Section title="Social media" hint="Paste the full page link. For WhatsApp use a link such as https://wa.me/233XXXXXXXXX">
        <div className="grid gap-4 sm:grid-cols-2">
          {SOCIALS.map((s) => (
            <Field key={s.key} id={`social-${s.key}`} label={s.label} placeholder="https://..." maxLength={500}
              value={f.socials[s.key] || ""} onChange={(e) => setF({ ...f, socials: { ...f.socials, [s.key]: e.target.value } })} />
          ))}
        </div>
      </Section>
      <Button onClick={save} disabled={saving} data-testid="footer-save-button">{saving ? "Saving…" : "Save footer"}</Button>
    </div>
  );
}
